/**
 * Agents API Service
 * 
 * Handles API calls for AI agent features.
 */

// Use Greenlight Analytics API key for AI agents
const AI_AGENT_API_KEY = process.env.REACT_APP_API_KEY || 'e9b87bc6e2ea92b5a1f8f44fb4557e0aed0d8f4efe58e50ca1c5c09e9e1fc4c2';

// Agent API endpoints
const AGENT_ENDPOINTS = {
  LOGLINE_ANALYSIS: 'https://greenlight-ai-api-4w4ng4wnuq-uc.a.run.app/api/v1/agents/story-analyzer/logline'
  // NOTE: If getting CORS errors in development, the API needs to add CORS headers:
  // - Access-Control-Allow-Origin: http://localhost:3000
  // - Access-Control-Allow-Methods: POST, OPTIONS
  // - Access-Control-Allow-Headers: X-API-Key, Content-Type
};

/**
 * Submit logline for AI analysis
 * 
 * @param {Object} data - Logline analysis request data
 * @param {string} data.logline - The film logline
 * @param {string} data.genre - Comma-separated genres
 * @param {string} data.distribution_strategy - Distribution strategy
 * @param {string} data.user_id - User identifier
 * @param {boolean} data.force_new - Force new analysis (always false)
 * @returns {Promise<Object>} Analysis results
 */
export const analyzeLogline = async (data) => {
  try {
    console.log('🚀 Submitting logline analysis:', {
      logline: data.logline.substring(0, 50) + '...',
      genre: data.genre,
      distribution_strategy: data.distribution_strategy,
      user_id: data.user_id
    });

    const response = await fetch(AGENT_ENDPOINTS.LOGLINE_ANALYSIS, {
      method: 'POST',
      headers: {
        'X-API-Key': AI_AGENT_API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    // Log response status
    console.log('📡 API Response Status:', response.status);

    // Handle non-OK responses
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });

      // Throw specific error based on status
      if (response.status === 401) {
        throw new Error('Authentication failed. Invalid API key.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(`API request failed: ${response.statusText}`);
      }
    }

    // Parse response
    const result = await response.json();
    console.log('✅ Analysis complete:', {
      success: result.success,
      hasData: !!result.data,
      cacheStatus: result.cache_info?.status
    });

    return result;
  } catch (error) {
    // Network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('🌐 Network error:', error);
      throw new Error('Network error. Please check your connection and try again.');
    }

    // Timeout errors
    if (error.name === 'AbortError') {
      console.error('⏱️ Request timeout:', error);
      throw new Error('Request timed out. The analysis is taking longer than expected.');
    }

    // Re-throw other errors
    console.error('💥 Unexpected error:', error);
    throw error;
  }
};

/**
 * Cancel an ongoing analysis request
 * Note: Actual cancellation depends on API support
 */
export const cancelAnalysis = () => {
  console.log('🛑 Analysis cancellation requested');
  // TODO: Implement AbortController if needed
};

const agentsApi = {
  analyzeLogline,
  cancelAnalysis
};

export default agentsApi;

